import { api } from './api';
import { grundHeader } from '../components/ui/loeschen-mit-grund-dialog';

export interface UnterrichtsformItem {
  id: number;
  name: string;
  typ: string;
  art_id?: number;
  dauer: number;
  max_schueler?: number;
  beschreibung?: string | null;
  aktiv: boolean;
}

export interface FachItem {
  id: number;
  name: string;
  emoji?: string | null;
  beschreibung?: string | null;
  aktiv: boolean;
  standort_ids?: number[];
}

export interface KursItem {
  id: number;
  name: string;
  fach_id: number;
  /** Unterrichtsform des Kurses — entscheidet über Preis und Honorar, nicht die Dauer. */
  unterrichtsform_id?: number | null;
  standort_id?: number | null;
  default_lehrer_id?: number | null;
  default_raum_id?: number | null;
  online_meeting_url?: string | null;
  dauer_min: number;
  farbe?: string | null;
  is_active: boolean;
}

export interface KursRegelItem {
  id: number;
  kurs_id: number;
  start_datum: string; // YYYY-MM-DD
  time_start: string;  // HH:MM:SS
  time_end: string;    // HH:MM:SS
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  intervall: number;
  byweekday?: string | null; // CSV 1..7
  until?: string | null;     // YYYY-MM-DD
  aktiv: boolean;
}

// Listen-Endpunkte des Backends liefern eine Seite ({ items, total, ... }); die
// Oberfläche arbeitet mit Arrays.
function pageItems<T>(res: { items?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(res)) return res;
  return Array.isArray(res?.items) ? res.items : [];
}

export const masterdataApi = {
  // Mit Standort nur die Formen, die dort angeboten werden (STO-3.2).
  unterrichtsformen: async (standortId?: number | null): Promise<UnterrichtsformItem[]> => {
    type Row = UnterrichtsformItem & { is_active?: boolean };
    const qs = standortId && standortId > 0 ? `&standortId=${encodeURIComponent(String(standortId))}` : '';
    const res = await api.get<{ items?: Row[] } | Row[]>(`/api/unterrichtsformen?limit=200${qs}`);
    // Das Backend nennt das Feld `is_active`, die Oberfläche `aktiv`.
    return pageItems(res).map((item) => ({ ...item, aktiv: item.aktiv ?? item.is_active ?? true }));
  },
  faecher: async (standortId?: number): Promise<FachItem[]> => {
    const qs = standortId ? `?standort_id=${encodeURIComponent(String(standortId))}` : '';
    return api.get<FachItem[]>(`/api/faecher${qs}`);
  },
};

export const kurseApi = {
  list: async (filters?: { teacherId?: number; fachId?: number; standortId?: number }): Promise<KursItem[]> => {
    const params = new URLSearchParams();
    if (filters?.teacherId) params.set('teacher_id', String(filters.teacherId));
    if (filters?.fachId) params.set('fach_id', String(filters.fachId));
    if (filters?.standortId) params.set('standort_id', String(filters.standortId));
    params.set('limit', '200');
    const res = await api.get<{ items?: KursItem[] } | KursItem[]>(`/api/kurse?${params.toString()}`);
    return pageItems(res);
  },
  get: async (id: number): Promise<KursItem> => {
    return api.get<KursItem>(`/api/kurse/${id}`);
  },
  create: async (payload: Partial<KursItem>): Promise<KursItem> => {
    return api.post<KursItem>('/api/kurse', payload);
  },
  update: async (id: number, payload: Partial<KursItem>): Promise<KursItem> => {
    return api.patch<KursItem>(`/api/kurse/${id}`, payload);
  },
  delete: async (id: number, grund: string): Promise<void> => {
    await api.delete(`/api/kurse/${id}`, grundHeader(grund));
  },
};

export const kursRegelnApi = {
  create: async (payload: Partial<KursRegelItem>): Promise<KursRegelItem> => {
    return api.post<KursRegelItem>('/api/kurs-regeln', payload);
  },
};

export const kursTeilnahmenApi = {
  create: async (payload: { kurs_id: number; person_id: number; subprofile_id?: string | number; effective_from?: string; effective_to?: string | null; status?: 'active' | 'paused' | 'ended' }): Promise<unknown> => {
    return api.post<unknown>('/api/kurs-teilnahmen', payload);
  },
};

export const materializeApi = {
  run: async (start: string, end: string, kursId?: number): Promise<{ insertedOrUpdated: number }> => {
    const qs = new URLSearchParams({ start, end, ...(kursId ? { kurs_id: String(kursId) } : {}) }).toString();
    return api.get<{ insertedOrUpdated: number }>(`/api/materialize?${qs}`);
  },
};

export const courseOccurrenceApi = {
  cancel: async (courseId: number, payload: {
    cancelDate: string;
    startTime: string;
    endTime: string;
    historyText?: string;
    alternative?: { date: string; startTime: string; endTime: string } | null;
    /** Wer sagt ab — nur eine Absage der Schule kann eine Gutschrift auslösen (T-0099). */
    cancelledBy?: 'schule' | 'schueler';
  }): Promise<{ ok: boolean; cancellationUnitId: number; replacementUnitId: number | null }> => {
    return api.post(`/api/kurse/${courseId}/cancel-occurrence`, payload);
  },
};

export interface PersonSuggestion {
  id: string;
  name: string;
  type: 'customer' | 'teacher' | 'subprofile' | string;
  parentId?: string | null;
  parentName?: string | null;
  email?: string | null;
  phone?: string | null;
  birthDate?: string | null;
  address?: string | null;
}

export const personSearchApi = {
  search: async (q: string, type?: 'customer' | 'teacher' | 'subprofile', limit = 10): Promise<PersonSuggestion[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (type) params.set('type', type);
    params.set('limit', String(limit));
    const data = await api.get<Array<Record<string, unknown>>>(`/api/personen?${params.toString()}`);
    return (data || []).map(p => ({
      id: String(p.id ?? p.wpUserId ?? ''),
      name: String(p.name ?? ''),
      type: String(p.type ?? ''),
      parentId: p.parentId ? String(p.parentId) : null,
      parentName: p.parentName ? String(p.parentName) : null,
      email: p.email ?? null,
      phone: p.phone ?? null,
      birthDate: p.birthDate ?? null,
      address: p.address ?? null,
    }));
  },
};


